/**
 * Shared Axios client for FxBazzar REST (`/api/v1`).
 * Login / demo / reset-password use services/authService.js (same origin in development).
 */
import axios from "axios";
import { isAuthCookieSession, refreshAuthCookiesOnce } from "../services/authService";
import {
  applyEncryptionHeaders,
  decryptFromEnvelopeJson,
  encryptToEnvelopeJson,
  encryptTokenToBase64Envelope,
  isApiCryptoEnabled,
  looksLikeEnvelope,
  isTimeInfoRequest,
} from "./apiCrypto";

function resolveApiOrigin() {
  const fromEnv = String(process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
  if (fromEnv) return fromEnv;
  // Empty = same-origin `/api/v1`. In `npm start` CRA setupProxy.js forwards to IIS
  // so HttpOnly fxb_access cookies stay first-party. Hitting LIVE_API from :3000 is
  // cross-site (http vs https) and SameSite=Lax cookies never ride → History 401.
  return "";
}

const API_BASE = resolveApiOrigin();

export const api = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  withCredentials: true,
  // TEMP debug: 15s was too short while F5 is paused on SaveOrder. Set back to 15000 after testing.
  timeout: 120000,
});

function readStoredToken() {
  try {
    const encoded = sessionStorage.getItem("TradeSession");
    if (encoded) {
      const d = JSON.parse(window.atob(encoded));
      const t = d?.token || d?.Token;
      if (t) return String(t).trim();
    }
  } catch {
    /* ignore */
  }
  for (const key of ["fxbazzar_user", "fxbazzar_admin"]) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) continue;
      const d = JSON.parse(raw);
      const t = d?.token || d?.Token;
      if (t) return String(t).trim();
    } catch {
      /* ignore */
    }
  }
  return "";
}

export const setAuthToken = (token) => {
  // Cookie mode: JWT is HttpOnly (fxb_access). A Bearer header would shadow the cookie
  // and 401 if that JWT is empty/expired.
  if (isAuthCookieSession()) {
    delete api.defaults.headers.common.Authorization;
    return;
  }
  const next = token || readStoredToken();
  if (next) {
    api.defaults.headers.common.Authorization = `Bearer ${next}`;
    return;
  }
  delete api.defaults.headers.common.Authorization;
};

api.interceptors.request.use(
  async (config) => {
    config.headers = config.headers || {};
    config.withCredentials = true;

    // Cookie is preferred. A stale Bearer in sessionStorage would shadow it and 401.
    // After cookie 401, __fxbBearerRetry sends the stored JWT once.
    if (isAuthCookieSession() && !config.__fxbBearerRetry) {
      delete config.headers.Authorization;
    } else {
      const storedToken = readStoredToken();
      if (storedToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
    }
    if (isApiCryptoEnabled() && !isTimeInfoRequest(config)) {
      const method = String(config.method || "get").toUpperCase();
      const hasJsonBodyMethod = method === "POST" || method === "PUT" || method === "PATCH";
      const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

      if (hasJsonBodyMethod && config.data != null && !isFormData) {
        // 401 retry reuses error.config — body may already be an encrypted envelope.
        let alreadyEncrypted = looksLikeEnvelope(config.data);
        if (!alreadyEncrypted && typeof config.data === "string") {
          try {
            alreadyEncrypted = looksLikeEnvelope(JSON.parse(config.data));
          } catch {
            alreadyEncrypted = false;
          }
        }
        if (!alreadyEncrypted) {
          const plain =
            typeof config.data === "string" ? config.data : JSON.stringify(config.data);
          config.data = JSON.parse(await encryptToEnvelopeJson(plain));
        }
        applyEncryptionHeaders(config.headers, { encryptedBody: true, encryptedResponse: true });
      } else {
        applyEncryptionHeaders(config.headers, { encryptedResponse: true });
      }

      const authHeader = String(config.headers.Authorization || "");
      if (authHeader.startsWith("Bearer ")) {
        const token = authHeader.slice(7).trim();
        if (token && !token.startsWith("eyJ")) {
          applyEncryptionHeaders(config.headers, { encryptedAuth: true });
        } else if (token) {
          const encToken = await encryptTokenToBase64Envelope(token);
          config.headers.Authorization = `Bearer ${encToken}`;
          applyEncryptionHeaders(config.headers, { encryptedAuth: true });
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

async function decryptIfEnvelope(data) {
  if (!isApiCryptoEnabled()) return data;
  let parsed = data;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return data;
    }
  }
  if (!looksLikeEnvelope(parsed)) return parsed;
  try {
    const plain = await decryptFromEnvelopeJson(parsed);
    return JSON.parse(plain);
  } catch {
    return data;
  }
}

async function decryptAxiosErrorBody(error) {
  if (!error?.response) return;
  error.response.data = await decryptIfEnvelope(error.response.data);
  const apiMsg = error.response.data?.message || error.response.data?.Message;
  if (apiMsg) error.message = String(apiMsg);
}

api.interceptors.response.use(
  async (response) => {
    response.data = await decryptIfEnvelope(response?.data);
    return response;
  },
  async (error) => {
    await decryptAxiosErrorBody(error);
    const status = error.response?.status;

    const original = error.config;
    const url = String(original?.url || "");
    const isRefreshCall = url.includes("user/refresh-token");

    if (status === 401 && original && !isRefreshCall && isAuthCookieSession()) {
      if (!original.__fxbRetried401) {
        original.__fxbRetried401 = true;
        const refreshed = await refreshAuthCookiesOnce();
        if (refreshed) {
          try {
            return await api.request(original);
          } catch (e) {
            await decryptAxiosErrorBody(e);
            error = e;
          }
        }
      }
      if (!original.__fxbBearerRetry) {
        const storedToken = readStoredToken();
        if (storedToken) {
          original.__fxbBearerRetry = true;
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${storedToken}`;
          try {
            return await api.request(original);
          } catch (e) {
            await decryptAxiosErrorBody(e);
            error = e;
          }
        }
      }
    }

    const finalStatus = error.response?.status;
    if (finalStatus && finalStatus !== 404) {
      console.log("API ERROR STATUS:", finalStatus);
    }
    if (finalStatus === 401) {
      console.warn("API 401 — session kept so History/Trade can retry");
    }
    return Promise.reject(error);
  }
);
